import {LineSeries} from '@visx/xychart';
import {scaleUtc, range} from 'd3';
import React from 'react';

import {assert} from 'Asserts';
import {DataType} from 'data-frames/DataFrameTypes';
import getTestProps from 'test/lib/cypress/getTestProps';
import {REGRESSION_LINE_COLOR} from 'ui-library/charting/internals/utils/Theme';
import {RegressionXAxisTSTypes} from 'ui-library/charting/statistics/regression/RegressionTypes';
import {AxisScale, AxisTypes, CartesianPoint, DatasetWithRegression} from 'ui-library/charting/Types';
import {assertNever} from 'util/Types';

import {useBenchlingChartContext} from '../BenchlingChartContext';
import {getChartMaskId} from './ChartMask';
import getCartesianAccessors from './utils/getCartesianAccessors';

export const NUM_INTERPOLATED_POINTS = 200;

export interface RegressionLineProps {
  axisTypes: AxisTypes;
  dataset: DatasetWithRegression;
  domain: Array<RegressionXAxisTSTypes>;
  scale: AxisScale;
  dataKey: string;
  color?: string;
}

export default function RegressionLine({
  axisTypes,
  dataset,
  domain,
  scale,
  dataKey,
  color = REGRESSION_LINE_COLOR,
}: RegressionLineProps): JSX.Element {
  const {clientId} = useBenchlingChartContext();
  const interpolatedPoints = interpolatePoints(
    domain,
    axisTypes.x,
    scale,
    dataset.regressionAnalysis.result.getEstimatedY
  );
  const dataAccessors = getCartesianAccessors(axisTypes);

  return (
    <LineSeries
      mask={`url(#${getChartMaskId(clientId)})`}
      data={interpolatedPoints}
      dataKey={dataKey}
      stroke={color}
      xAccessor={dataAccessors.xAccessor}
      yAccessor={dataAccessors.yAccessor}
      {...getTestProps({component: 'RegressionLine', element: 'root'})}
    />
  );
}

/**
 * Interpolates points based on the given domain.
 *
 * If the domain is over numbers, interpolates NUM_INTERPOLATED_POINTS points, including the min and max
 * values of the domain.
 *
 * If the domain is over strings, interpolates a point for each value in the domain.
 */
export function interpolatePoints(
  domain: Array<RegressionXAxisTSTypes>,
  domainType: DataType,
  scale: AxisScale,
  getEstimatedY: (x: RegressionXAxisTSTypes) => number
): Array<CartesianPoint<RegressionXAxisTSTypes, number>> {
  let xRange;
  if (domainType === DataType.NUMBER) {
    const [domainMin, domainMax] = [domain[0], domain[domain.length - 1]] as Array<number>;
    if (scale === AxisScale.LINEAR) {
      const stepSize = (domainMax - domainMin) / (NUM_INTERPOLATED_POINTS - 1);
      xRange = range(domainMin, domainMax + stepSize, stepSize);
    } else if (scale === AxisScale.LOG) {
      // When the axis scale is log, we want to sample more points near the bottom of the domain, since
      // the resolution of log(x) is higher when x is lower.
      //
      // For example, say:
      // - domain = [1, 100]. This may mean gridlines are placed at x = (1, 10, 100),
      // - NUM_INTERPOLATED_POINTS = 9
      // We want to interpolate with an equal number of points in the [1, 10) interval
      // as in the [10, 100) interval, with 4 points in each (plus a point at x=100).
      //
      // We first compute the stepsize:
      // stepsize = (log(100) - log(1)) / (9 - 1)
      //          = (2 - 0) / 8
      //          = 0.25
      //
      // We then generate an array of points between 0 and 2 with step size 0.25:
      // [0, 0.25, ..., 1.75, 2]
      //
      // Finally, to return this to the original domain, we exponentiate the array by applying 10**x to each
      // element:
      // [10^0, 10^0.25, ..., 10^1.75, 10^2]
      // = [1, 1.778, ..., 56.23, 100]
      const stepSize = (Math.log10(domainMax) - Math.log10(domainMin)) / (NUM_INTERPOLATED_POINTS - 1);
      xRange = range(Math.log10(domainMin), Math.log10(domainMax) + stepSize, stepSize).map((x) =>
        Math.pow(10, x)
      );
    } else {
      assert(false, `Got unexpected axis scale ${assertNever(scale)}`);
    }
  } else if (domainType === DataType.DATE_OR_TIMESTAMP) {
    xRange = [
      // scaleUtc.ticks doesn't guarantee returning the domain endpoints, so make sure they're included
      domain[0],
      ...scaleUtc()
        .domain(domain as Array<Date>)
        .ticks(NUM_INTERPOLATED_POINTS - 2),
      domain[1],
    ];
  } else {
    xRange = domain;
  }

  return xRange.map((x) => ({
    x,
    y: getEstimatedY(x),
  }));
}
